import {Injectable} from '@angular/core';
import {TrophyData} from "../interfaces/trophy-data";
import {BehaviorSubject, take, takeWhile} from "rxjs";
import {DataService} from "./data.service";
import {GameTrophyStatus} from "../enums/game-trophy-status";
import {TrophyLevel} from "../enums/trophy-level";

@Injectable({
  providedIn: 'root'
})
export class TrophyRepositoryService {

  private _trophies: TrophyData[] = [];
  private _onServerDataLoaded = new BehaviorSubject<boolean>(false);
  constructor(private data: DataService) {
    this.requestServerTrophyData();
  }

  private requestServerTrophyData(): void {
    this.data.getGameTrophies().pipe(take(1)).subscribe((trophies) => {
      this._trophies = trophies.map(el => {
        return {
          id: parseInt(el.id),
          name: el.trophyName ?? '',
          description: el.description ?? '',
          status: el.trophyStatus === "NORMAL" ? GameTrophyStatus.NORMAL : GameTrophyStatus.HIDDEN
        }
      });

      this._onServerDataLoaded.next(true);
    })
  }

  async getTrophyById(id: number): Promise<TrophyData> {
    // console.log(this._trophies);
    if(!this._onServerDataLoaded.value) {
      await new Promise((resolve, reject) => {
        this._onServerDataLoaded.pipe(takeWhile((state) => !state, true)).subscribe((newVal) => {
          if(newVal) resolve(true);
        });
      });
    }
    const trophy = this._trophies.filter((item) => item.id === id);
    if(trophy.length) {
      return trophy[0]
    } else {
      throw new Error("Trophy with id " + id + " was not found!");
    }
  }

  getTrophyIcon(trophy: TrophyData, skipHidden = false): string {
    if(trophy.status === GameTrophyStatus.HIDDEN && !skipHidden) {
      return "/assets/img/trophies/main/Versteckt.svg";
    } else {
      return this.getTrophyIconById(trophy.id);
    }

  }

  getTrophyIconById(trophyid: number) {
    switch(trophyid) {
      case 1:
        return "/assets/img/trophies/main/Motiv=01 In der Natur.svg";
      case 2:
        return "/assets/img/trophies/main/Motiv=02 Mit Genuss.svg";
      case 3:
        return "/assets/img/trophies/main/Motiv=03 Mit Kreativitaet.svg";
      case 4:
        return "/assets/img/trophies/main/Motiv=04 Am Bau.svg";
      case 5:
        return "/assets/img/trophies/main/Motiv=05 Unter Strom.svg";
      case 6:
        return "/assets/img/trophies/main/Motiv=06 In IT Forschung.svg";
      case 7:
        return "/assets/img/trophies/main/Motiv=07 In Buero Handel.svg";
      case 8:
        return "/assets/img/trophies/main/Motiv=08 In Medien Kultur.svg";
      case 9:
        return "/assets/img/trophies/main/Motiv=09 Mit Menschen.svg";
      case 10: case 11: case 12:
        return "/assets/img/trophies/main/Motiv=Berufe.svg";
      case 13: case 14:
        return "/assets/img/trophies/main/Motiv=Berufsbilder.svg";
      case 15:
        return "/assets/img/trophies/main/Motiv=Buchungen_.svg";
      case 16: case 17:
        return "/assets/img/trophies/main/Motiv=Unternehmen.svg";
      case 18: case 19:
        return "/assets/img/trophies/main/Motiv=Schulen.svg";
      case 20: case 21:
        return "/assets/img/trophies/main/Motiv=Institutionen.svg";
      case 22: case 23: case 24:
        return "/assets/img/trophies/main/Motiv=Quiz.svg";
      case 25: case 26: case 27:
        return "/assets/img/trophies/main/Motiv=Erkenne das Bild.svg";
      case 28: case 29: case 30:
        return "/assets/img/trophies/main/Motiv=Anbieter-Quiz.svg";
      case 31: case 32: case 33:
        return "/assets/img/trophies/main/Motiv=Unterrichtsidee.svg";
      case 34:
        return "/assets/img/trophies/main/Motiv=Buchungen_.svg";
      case 35: case 36:
        return "/assets/img/trophies/main/Motiv=Portraits.svg";
      case 37: case 38: case 39: case 40: case 41: case 42:
      case 43: case 44: case 45:
        /* TODO: temporary set icon Buchungen - Manuel */
        return "/assets/img/trophies/main/Motiv=Buchungen_.svg";       
      default:
        console.warn("Trophy icon for trophy with id " + trophyid + " was not found!");
        return "";
    }
  }

  getTrophySuccessIconById(trophyid: number) {
    switch(trophyid) {
      case 1:
        return "/assets/img/trophies/success/Trophy Success Berufe in der Natur.svg";
      case 2:
        return "/assets/img/trophies/success/Trophy Success Berufe mit Genuss.svg";
      case 3:
        return "/assets/img/trophies/success/Trophy Success Berufe mit Kreativität.svg";
      case 4:
        return "/assets/img/trophies/success/Trophy Success Berufe am Bau.svg";
      case 5:
        return "/assets/img/trophies/success/Trophy Success Berufe unter Strom.svg";
      case 6:
        return "/assets/img/trophies/success/Trophy Success Berufe in IT & Forschung.svg";
      case 7:
        return "/assets/img/trophies/success/Trophy Success Berufe in Büro & Handel.svg";
      case 8:
        return "/assets/img/trophies/success/Trophy Success Berufe in Medien & Kultur.svg";
      case 9:
        return "/assets/img/trophies/success/Trophy Success Berufe mit Menschen.svg";
      case 10:
        return "/assets/img/trophies/success/Trophy Success Berufe Profi.svg";
      case 11:
        return "/assets/img/trophies/success/Trophy Success Berufe Meister.svg";
      case 12:
        return "/assets/img/trophies/success/Trophy Success Berufe Sensei.svg";
      case 13:
        return "/assets/img/trophies/success/Trophy Success Berufe Strawanzer.svg";
      case 14:
        return "/assets/img/trophies/success/Trophy Success Berufe Weltenbummler.svg";
      case 15:
        return "/assets/img/trophies/success/Trophy Success Alle Einblicke gesehen.svg";
      case 16:
        return "/assets/img/trophies/success/Trophy Success Unternehmens Meister.svg";
      case 17:
        return "/assets/img/trophies/success/Trophy Success Unternehmens Sensei.svg";
      case 18:
        return "/assets/img/trophies/success/Trophy Success Schulen Profi.svg";
      case 19:
        return "/assets/img/trophies/success/Trophy Success Schulen Sensei.svg";
      case 20:
        return "/assets/img/trophies/success/Trophy Success BO Institution Profi.svg";
      case 21:
        return "/assets/img/trophies/success/Trophy Success BO Institution Sensei.svg";
      case 22:
        return "/assets/img/trophies/success/Trophy Success Quiz Show Profi.svg";
      case 23:
        return "/assets/img/trophies/success/Trophy Success Quiz Show Meister.svg";
      case 24:
        return "/assets/img/trophies/success/Trophy Success Quiz Show Sensei.svg";
      case 25:
        return "/assets/img/trophies/success/Trophy Success Erkenne Den Beruf Profi.svg";
      case 26:
        return "/assets/img/trophies/success/Trophy Success Erkenne Den Beruf Meister.svg";
      case 27:
        return "/assets/img/trophies/success/Trophy Success Erkenne Den Beruf Sensei.svg";
      case 28:
        return "/assets/img/trophies/success/Trophy Success Anbieter-Quiz Profi.svg";
      case 29:
        return "/assets/img/trophies/success/Trophy Success Anbieter-Quiz Meister.svg";
      case 30:
        return "/assets/img/trophies/success/Trophy Success Anbieter-Quiz Sensei.svg";
      case 31:
        return "/assets/img/trophies/success/Trophy Success Unterrichtsideen Profi.svg";
      case 32:
        return "/assets/img/trophies/success/Trophy Success Unterrichtsideen Meister.svg";
      case 33:
        return "/assets/img/trophies/success/Trophy Success Unterrichtsideen Sensei.svg";
      case 34:
        return "/assets/img/trophies/success/Trophy Success Berufe Profi.svg";
      case 35: case 36:
        /* TODO: temporary set success icon to berufe profi - Manuel */
        return "/assets/img/trophies/success/Trophy Success Berufe Profi.svg";
      case 37: case 38: case 39: case 40: case 41: case 42:
      case 43: case 44: case 45:
        /* TODO: temporary set success icon to berufe profi - Manuel */
        return "/assets/img/trophies/success/Trophy Success Berufe Profi.svg";         
      default:
        console.warn("Trophy icon for trophy with id " + trophyid + " was not found!");
        return "";
    }
  }

  getTrophyLevel(trophy: TrophyData): TrophyLevel {
    const nameLc = trophy.name.toLowerCase();
    if(nameLc.indexOf("profi") !== -1 || nameLc.indexOf("strawanzer") !== -1) return TrophyLevel.PROFI;
    if(nameLc.indexOf("champion") !== -1 || nameLc.indexOf("meister") !== -1) return TrophyLevel.CHAMPION;
    if(nameLc.indexOf("sensei") !== -1 || nameLc.indexOf("legende") !== -1 || nameLc.indexOf("weltenbummler") !== -1 || trophy.id === 15) return TrophyLevel.SENSEI;
    return TrophyLevel.BASIC;
  }

  getTrophyBackgroundSrcByLevel(level: TrophyLevel): string {
    switch(level) {
      case TrophyLevel.BASIC:
        return '/assets/img/trophies/backgrounds/Background=Kategorie.svg';
      case TrophyLevel.PROFI:
        return '/assets/img/trophies/backgrounds/Background=Profi.svg';
      case TrophyLevel.CHAMPION:
        return '/assets/img/trophies/backgrounds/Background=Meister.svg';
      case TrophyLevel.SENSEI:
        return '/assets/img/trophies/backgrounds/Background=Sensei.svg';
    }
  }
}

// class Promisable<T> {
//   private _readySubject: BehaviorSubject<boolean>;
//   private _func: () => T;
//
//   constructor(readySubject: BehaviorSubject<boolean>, func: () => T) {
//     this._readySubject = readySubject;
//     this._func = func;
//
//   }
//
//   async get() {
//     if(!this._readySubject.value) {
//       await new Promise((resolve, reject) => { this._readySubject.pipe(takeWhile((state) => !state, true)).subscribe((newVal) => { resolve(true);})});
//     }
//     return this._func();
//   }
// }
